import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { F0Dialog, F0Text, F0OneIcon } from '@factorialco/f0-react'
import { useAiChat } from 'modules/ai/chat/useAiChat'
import { Stack } from '@factorialco/f0-react/dist/experimental'
import {
  CreateTicketingResolverTeamDocument,
  CreateTicketingTicketCategoryDocument,
  TicketingResolverTeamsDocument,
  type TicketingResolverTeamsQuery,
  type TicketingResolverTeamsQueryVariables,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { useActivities } from 'modules/core/lib/activities/provider'
import { TeamSelector, type Team } from 'modules/ticketing/components/TeamSelector'
import { useTicketingPolicies } from 'modules/ticketing/hooks/useTicketingPolicies'
import { trackSelectTemplate, trackCompleteQuickSetup } from 'modules/ticketing/lib/tracking'
import { useQuery } from 'modules/core/hooks/useQuery'

import { getTemplates, type ResolvedTemplate } from './templates'

interface Props {
  onClose: () => void
  onComplete?: () => void
}

type Step = 'template' | 'team' | 'success'

export const QuickSetupDialog = ({ onClose, onComplete }: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { openActivity } = useActivities()
  const { sendMessage, setOpen: setAiChatOpen } = useAiChat()
  const { aiEnabled } = useTicketingPolicies()

  const [step, setStep] = useState<Step>('template')
  const [selectedTemplate, setSelectedTemplate] = useState<ResolvedTemplate | null>(null)
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const templates = useMemo(() => getTemplates(), [])

  const { loading: resolverTeamsLoading, refetch: refetchResolverTeams } = useQuery<
    TicketingResolverTeamsQuery,
    TicketingResolverTeamsQueryVariables
  >(TicketingResolverTeamsDocument, { variables: { perPage: 100 } })

  const [createResolverTeam] = useMutation(CreateTicketingResolverTeamDocument, {
    extractorFn: (data) => data.ticketingMutations.createTicketingResolverTeam,
  })

  const [createCategory] = useMutation(CreateTicketingTicketCategoryDocument, {
    extractorFn: (data) => data.ticketingMutations.createTicketingTicketCategory,
  })

  const handleSelectTemplate = useCallback((template: ResolvedTemplate) => {
    setSelectedTemplate(template)
    trackSelectTemplate({ template_id: template.id })
    setStep('team')
  }, [])

  const handleBack = useCallback(() => {
    setStep('template')
    setSelectedTeam(null)
    setError(null)
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!selectedTemplate || !selectedTeam) return

    setSubmitting(true)
    setError(null)

    try {
      const rtResult = await createResolverTeam({
        variables: { teamId: selectedTeam.id },
      })
      const rtHandled = handleResult(rtResult, () => {})

      let resolverTeamId: string | undefined

      if (rtHandled.success && rtHandled.data?.resolverTeam?.id) {
        resolverTeamId = rtHandled.data.resolverTeam.id
      } else {
        // Team might already be a resolver team — refetch and look it up
        const { data: freshTeams } = await refetchResolverTeams()
        const existingTeam = freshTeams?.ticketing?.resolverTeamsConnection?.nodes?.find(
          (rt) => String(rt.teamId) === String(selectedTeam.id)
        )
        if (existingTeam) {
          resolverTeamId = existingTeam.id
        } else {
          setError(i18n.t('ticketing.onboarding.setup_error'))
          setSubmitting(false)
          return
        }
      }

      for (const category of selectedTemplate.categories) {
        const catResult = await createCategory({
          variables: {
            name: category.name,
            description: category.description,
            resolverTeamId,
            priorityMode: 'manual',
            slaMode: 'no_sla',
            assignmentMode: 'unassigned',
          },
        })
        const catHandled = handleResult(catResult, () => {})
        if (!catHandled.success) {
          // Skip duplicate category names (already exists from a previous attempt)
          const errors = catHandled.errors ?? []
          const isDuplicate = errors.some(
            (e) =>
              ('message' in e &&
                (e.message?.toLowerCase().includes('taken') ||
                  e.message?.toLowerCase().includes('already') ||
                  e.message?.toLowerCase().includes('unique'))) ||
              ('messages' in e &&
                e.messages?.some(
                  (m: string) =>
                    m.toLowerCase().includes('taken') ||
                    m.toLowerCase().includes('already') ||
                    m.toLowerCase().includes('unique')
                ))
          )
          if (!isDuplicate) {
            setError(i18n.t('ticketing.onboarding.setup_error'))
            setSubmitting(false)
            return
          }
        }
      }

      trackCompleteQuickSetup({ template_id: selectedTemplate.id })
      setStep('success')
      onComplete?.()
    } catch {
      setError(i18n.t('ticketing.onboarding.setup_error'))
    } finally {
      setSubmitting(false)
    }
  }, [selectedTemplate, selectedTeam, createResolverTeam, createCategory, refetchResolverTeams])

  const handleCreateTicket = useCallback(() => {
    onClose()
    openActivity('ticketing.createTicket')
  }, [onClose, openActivity])

  const handleCreateWithOne = useCallback(() => {
    onClose()
    setAiChatOpen(true)
    sendMessage(i18n.t('ticketing.onboarding.one_create_ticket_prompt'))
  }, [onClose, sendMessage, setAiChatOpen])

  const handleGoToSettings = useCallback(() => {
    onClose()
    navigate(navigation.settings.ticketing.ticketCategories.fullPath())
  }, [onClose, navigate, navigation])

  if (step === 'template') {
    return (
      <F0Dialog
        isOpen
        width="md"
        title={i18n.t('ticketing.onboarding.select_template_title')}
        onClose={onClose}
        secondaryAction={{
          label: i18n.t('common.cancel'),
          onClick: onClose,
        }}
      >
        <Stack paddingX="p-4" paddingY="p-4" gap="4">
          <F0Text
            variant="description"
            content={i18n.t('ticketing.onboarding.select_template_description')}
          />
          {templates.map((template) => (
            <button
              key={template.id}
              type="button"
              className="cursor-pointer rounded-lg border border-solid border-f1-border bg-f1-background p-4 text-left transition-colors hover:bg-f1-background-secondary"
              onClick={() => handleSelectTemplate(template)}
            >
              <Stack gap="1">
                <F0Text variant="label" content={template.name} />
                <F0Text variant="description" content={template.description} />
                <F0Text
                  variant="small"
                  content={template.categories.map((c) => c.name).join(' · ')}
                  color="secondary"
                />
              </Stack>
            </button>
          ))}
        </Stack>
      </F0Dialog>
    )
  }

  if (step === 'team') {
    return (
      <F0Dialog
        isOpen
        width="sm"
        title={i18n.t('ticketing.onboarding.assign_team_title')}
        onClose={onClose}
        primaryAction={{
          label: i18n.t('common.continue'),
          onClick: handleSubmit,
          disabled: !selectedTeam || submitting || resolverTeamsLoading,
          loading: submitting,
        }}
        secondaryAction={{
          label: i18n.t('common.back'),
          onClick: handleBack,
        }}
      >
        <Stack paddingX="p-4" paddingY="p-4" gap="4">
          <F0Text
            variant="description"
            content={i18n.t('ticketing.onboarding.assign_team_description')}
          />
          {selectedTemplate && (
            <Stack gap="1">
              <F0Text variant="label" content={selectedTemplate.name} />
              <F0Text
                variant="small"
                content={selectedTemplate.categories.map((c) => c.name).join(' · ')}
                color="secondary"
              />
            </Stack>
          )}
          <TeamSelector
            value={selectedTeam}
            onSelect={setSelectedTeam}
            label={i18n.t('ticketing.settings.resolver_teams.select_team')}
            mandatory
          />
          {error && <F0Text content={error} color="critical" />}
        </Stack>
      </F0Dialog>
    )
  }

  // step === 'success'
  return (
    <F0Dialog
      isOpen
      width="md"
      title={i18n.t('ticketing.onboarding.success_title')}
      onClose={onClose}
      primaryAction={[
        {
          value: 'create_with_one',
          label: i18n.t('ticketing.onboarding.success_create_with_one'),
          icon: F0OneIcon,
          onClick: handleCreateWithOne,
        },
        {
          value: 'create_ticket',
          label: i18n.t('ticketing.onboarding.success_create_ticket'),
          onClick: handleCreateTicket,
        },
      ].filter((action) => {
        if (action.value === 'create_with_one') return aiEnabled
        return true
      })}
      secondaryAction={{
        label: i18n.t('ticketing.onboarding.success_go_to_settings'),
        onClick: handleGoToSettings,
      }}
    >
      <Stack paddingX="p-4" paddingY="p-4" gap="4">
        <F0Text
          variant="description"
          content={i18n.t('ticketing.onboarding.success_description')}
        />
        {selectedTemplate && (
          <Stack gap="2">
            <F0Text variant="label" content={selectedTemplate.name} />
            <Stack gap="1">
              {selectedTemplate.categories.map((category) => (
                <Stack key={category.name} gap="0">
                  <F0Text variant="body" content={category.name} />
                  <F0Text variant="small" content={category.description} color="secondary" />
                </Stack>
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </F0Dialog>
  )
}
